import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

/**
 * MarkdownContent — Safe Markdown renderer for editor content (news posts,
 * page sections, etc.). Uses GFM for lists / tables / strikethrough / autolinks.
 *
 * Why not a heavy WYSIWYG?
 *   Authors write a small amount of formatted text. Markdown stays portable
 *   (plain in DB, no fragile HTML), is human-editable in any tool, and
 *   `react-markdown` sandboxes HTML by default so XSS is not a risk.
 *
 * Tailwind styling is applied via a "prose" wrapper — the parent decides the
 * brand voice by passing `className`. Defaults aim for the Zebra One serif
 * editorial feel.
 */
const MarkdownContent = ({ children, className = '' }) => {
  if (!children) return null;

  return (
    <div className={`zog-prose ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // External links open in a new tab; auto rel for security
          a: ({ node, ...props }) => (
            <a
              {...props}
              target={props.href?.startsWith('http') ? '_blank' : undefined}
              rel={props.href?.startsWith('http') ? 'noopener noreferrer' : undefined}
              className="underline underline-offset-4 hover:text-foreground transition-colors"
            />
          ),
          h1: ({ node, ...props }) => (
            <h1 {...props} className="font-serif text-3xl mt-6 mb-3 leading-tight" />
          ),
          h2: ({ node, ...props }) => (
            <h2 {...props} className="font-serif text-2xl mt-5 mb-3 leading-tight" />
          ),
          h3: ({ node, ...props }) => (
            <h3 {...props} className="font-serif text-xl mt-4 mb-2 leading-tight" />
          ),
          p: ({ node, ...props }) => (
            <p {...props} className="mb-4 last:mb-0 leading-relaxed" />
          ),
          ul: ({ node, ...props }) => (
            <ul {...props} className="list-disc ml-6 mb-4 space-y-1" />
          ),
          ol: ({ node, ...props }) => (
            <ol {...props} className="list-decimal ml-6 mb-4 space-y-1" />
          ),
          blockquote: ({ node, ...props }) => (
            <blockquote
              {...props}
              className="border-l-2 border-gray-300 pl-4 italic text-gray-600 my-4"
            />
          ),
          code: ({ inline, children, ...props }) =>
            inline ? (
              <code {...props} className="px-1.5 py-0.5 bg-gray-100 rounded text-sm font-mono">
                {children}
              </code>
            ) : (
              <pre className="bg-gray-100 rounded p-3 overflow-x-auto text-sm font-mono mb-4">
                <code {...props}>{children}</code>
              </pre>
            ),
          hr: () => <hr className="my-6 border-gray-200" />,
        }}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownContent;
